import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Check, ArrowCounterClockwise, Trash, EnvelopeSimple, PaperPlaneTilt, ChatText } from "@phosphor-icons/react";
import { SavedRepliesManager, fillReply } from "@/components/admin/SavedReplies";

const FILTERS = [["open", "Open"], ["handled", "Handled"], ["all", "All"]];
const btn = "label-mono text-[0.6rem] px-3 py-1.5 border border-black/15 hover:border-primary hover:text-primary transition-colors inline-flex items-center gap-1";
const field = "w-full bg-white/5 border border-white/20 px-3 py-2 text-sm focus:border-primary outline-none transition-colors";

const Composer = ({ m, replies, onSent, onCancel }) => {
  const [subject, setSubject] = useState(`Re: ${m.topic}${m.order_ref ? ` (${m.order_ref})` : ""}`);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const send = async () => {
    if (!body.trim()) { toast.error("Write a reply or pick a saved one"); return; }
    setSending(true);
    try {
      await api.post(`/admin/messages/${m.id}/reply`, { subject, body });
      toast.success(`Reply sent to ${m.email}`); onSent();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send reply"); } finally { setSending(false); }
  };
  return (
    <div data-testid={`reply-composer-${m.id}`} className="mt-4 border-t border-black/10 pt-4 space-y-3">
      {replies.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {replies.map((r) => (
            <button key={r.id} data-testid={`use-saved-reply-${r.id}`} onClick={() => { if (!body.trim() || window.confirm("Replace what you've written with this saved reply?")) setBody(fillReply(r.body, m)); }}
              className="label-mono text-[0.55rem] px-2.5 py-1.5 border border-primary/40 text-primary hover:bg-primary hover:text-black transition-colors">{r.title}</button>
          ))}
        </div>
      )}
      <input data-testid={`reply-subject-${m.id}`} value={subject} maxLength={200} onChange={(e) => setSubject(e.target.value)} className={field} />
      <textarea data-testid={`reply-body-${m.id}`} rows={7} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Tap a saved reply above or write your own…" className={`${field} resize-y`} />
      <div className="flex gap-2">
        <button data-testid={`reply-send-${m.id}`} onClick={send} disabled={sending} className="label-mono text-[0.6rem] px-4 py-2 bg-primary text-black hover:bg-[#E6C766] transition-colors inline-flex items-center gap-1 disabled:opacity-50"><PaperPlaneTilt size={12} /> {sending ? "Sending…" : "Send reply"}</button>
        <button data-testid={`reply-cancel-${m.id}`} onClick={onCancel} className={btn}>Cancel</button>
      </div>
    </div>
  );
};

const MessageCard = ({ m, replies, onToggle, onDelete, reload }) => {
  const [composing, setComposing] = useState(false);
  return (
    <div data-testid={`message-${m.id}`} className={`border p-5 bg-surface ${m.handled ? "border-black/10" : "border-primary/40"}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className={m.handled ? "opacity-70" : ""}>
          <p className="font-medium">{m.name} <span className="text-muted-foreground text-sm font-normal">· {m.email}</span></p>
          <p className="label-mono text-[0.6rem] text-muted-foreground mt-1">
            <span className="text-primary">{m.topic}</span>{m.order_ref ? ` · ${m.order_ref}` : ""} · {new Date(m.created_at).toLocaleString()}
          </p>
        </div>
        <div className="flex gap-2">
          <button data-testid={`message-reply-${m.id}`} onClick={() => setComposing(!composing)} className={btn}><EnvelopeSimple size={12} /> Reply</button>
          <button data-testid={`message-toggle-${m.id}`} onClick={() => onToggle(m)} className={btn}>
            {m.handled ? <><ArrowCounterClockwise size={12} /> Reopen</> : <><Check size={12} /> Mark handled</>}
          </button>
          <button data-testid={`message-delete-${m.id}`} onClick={() => onDelete(m)} className="p-1.5 text-muted-foreground hover:text-destructive transition-colors"><Trash size={14} /></button>
        </div>
      </div>
      <p className="text-sm text-muted-foreground mt-3 whitespace-pre-wrap break-words">{m.message}</p>
      {m.replies?.map((r, i) => (
        <div key={i} data-testid={`message-sent-reply-${m.id}-${i}`} className="mt-3 ml-4 border-l-2 border-primary/50 pl-4">
          <p className="label-mono text-[0.55rem] text-primary">You replied · {new Date(r.sent_at).toLocaleString()}</p>
          <p className="text-sm text-muted-foreground whitespace-pre-wrap break-words mt-1">{r.body}</p>
        </div>
      ))}
      {composing && <Composer m={m} replies={replies} onCancel={() => setComposing(false)} onSent={() => { setComposing(false); reload(); }} />}
    </div>
  );
};

export const AdminMessages = () => {
  const [msgs, setMsgs] = useState([]);
  const [replies, setReplies] = useState([]);
  const [filter, setFilter] = useState("open");
  const [managing, setManaging] = useState(false);
  const load = () => api.get("/admin/messages").then(({ data }) => setMsgs(data)).catch(() => toast.error("Could not load messages"));
  const loadReplies = () => api.get("/admin/saved-replies").then(({ data }) => setReplies(data)).catch(() => {});
  useEffect(() => { load(); loadReplies(); }, []);
  const toggle = async (m) => {
    try { await api.put(`/admin/messages/${m.id}`, { handled: !m.handled }); load(); } catch { toast.error("Could not update message"); }
  };
  const remove = async (m) => {
    if (!window.confirm(`Delete message from ${m.name}?`)) return;
    try { await api.delete(`/admin/messages/${m.id}`); toast.success("Message deleted"); load(); } catch { toast.error("Could not delete message"); }
  };
  const shown = msgs.filter((m) => filter === "all" || (filter === "handled" ? m.handled : !m.handled));
  const openCount = msgs.filter((m) => !m.handled).length;
  return (
    <div data-testid="admin-messages" className="space-y-4">
      <div className="flex flex-wrap justify-between gap-2">
        <div className="flex gap-2">
          {FILTERS.map(([k, l]) => (
            <button key={k} data-testid={`messages-filter-${k}`} onClick={() => setFilter(k)}
              className={`label-mono px-3 py-1.5 border text-[0.6rem] transition-colors ${filter === k ? "border-primary text-primary bg-primary/10" : "border-black/10 text-muted-foreground hover:border-black/30"}`}>
              {l}{k === "open" && openCount ? ` (${openCount})` : ""}
            </button>
          ))}
        </div>
        <button data-testid="manage-saved-replies" onClick={() => setManaging(!managing)} className={btn}><ChatText size={12} /> Saved replies ({replies.length})</button>
      </div>
      {managing && <SavedRepliesManager replies={replies} reload={loadReplies} onClose={() => setManaging(false)} />}
      {shown.length === 0 && <p data-testid="messages-empty" className="text-muted-foreground font-mono text-sm">No {filter === "all" ? "" : filter} messages.</p>}
      {shown.map((m) => <MessageCard key={m.id} m={m} replies={replies} onToggle={toggle} onDelete={remove} reload={load} />)}
    </div>
  );
};
